import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTags } from '../../features/tags';
import { brandConfig } from '../../config/brands';
import { CreateTagModal } from './CreateTagModal';
import { useIsMobile } from '../../hooks/useIsMobile';
import styles from './TagSelector.module.css';
import type { Tag } from '../../features/tags/types';

type Props = {
  value?: Tag[];  // Tag[]に変更
  disabled?: boolean;
  onChange: (tags: Tag[]) => void;  // Tag[]に変更
};

export function TagSelector({ value, disabled, onChange }: Props) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [createModalPosition, setCreateModalPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tagsQuery = useTags();
  // ドロップダウン / 新規作成モーダルの配置をスマホ向けに切り替える判定
  // (CSS の @media (max-width: 768px) と同期)
  const isMobileView = useIsMobile();

  const selectedTags = useMemo(() => value ?? [], [value]);

  // 利用可能なタグ = 全タグ - 選択済みタグ
  const availableTags = useMemo(() => {
    if (!tagsQuery.data) return [];
    const selectedIds = new Set(selectedTags.map(t => t.id));
    return tagsQuery.data.filter(tag => !selectedIds.has(tag.id));
  }, [tagsQuery.data, selectedTags]);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // ドロップダウンまたはボタンの外側をクリックした場合に閉じる
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      const isInsideButton = buttonRef.current && buttonRef.current.contains(target);

      if (!isInsideDropdown && !isInsideButton) {
        setDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleAddTag = (tag: Tag) => {
    const alreadySelected = selectedTags.some(t => t.id === tag.id);
    if (!alreadySelected) {
      onChange([...selectedTags, tag]);
    }
    // ドロップダウンは閉じない（複数タグを連続追加可能に）
  };

  const handleRemoveTag = (tagId: string) => {
    onChange(selectedTags.filter(t => t.id !== tagId));
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isDropdownOpen && buttonRef.current) {
        // ボタンの位置を取得してドロップダウンの位置を計算
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 280;
        const dropdownMaxHeight = 350; // ヘッダー、タグリスト、divider、新規作成ボタンの合計
        const spacing = 8;
        const viewportPadding = 8; // 画面端からの最小余白

        // 基本位置：ボタンの下に表示（スマホ）またはボタンの上端揃え（PC）
        let top = isMobileView ? rect.bottom + spacing : rect.top;

        // 画面下端を超えないかチェック
        const bottomOverflow = top + dropdownMaxHeight - window.innerHeight + viewportPadding;

        if (bottomOverflow > 0) {
          top = top - bottomOverflow;
          if (top < viewportPadding) {
            top = viewportPadding;
          }
        }

        // 水平位置：スマホでは画面中央、PCではボタンの左側
        let left: number;
        if (isMobileView) {
          left = Math.max(viewportPadding, (window.innerWidth - dropdownWidth) / 2);
        } else {
          left = rect.left - dropdownWidth - spacing;
          // PC でも画面左端を超えないようにする
          if (left < viewportPadding) {
            left = rect.right + spacing;
          }
        }

        setDropdownPosition({ top, left });
      }
      setDropdownOpen(!isDropdownOpen);
    }
  };

  const handleCreateNew = () => {
    const createModalWidth = 280;
    const spacing = 8;
    const viewportPadding = 8;

    let left: number;
    if (isMobileView) {
      left = Math.max(viewportPadding, (window.innerWidth - createModalWidth) / 2);
    } else {
      left = dropdownPosition.left - createModalWidth - spacing;
    }

    setCreateModalPosition({
      top: dropdownPosition.top,
      left,
    });

    // ドロップダウンは開いたままにする
    setCreateModalOpen(true);
  };

  const handleTagCreated = async (tagName: string) => {
    // 新規作成したタグを自動的に追加（refetch完了後の結果を使う）
    const result = await tagsQuery.refetch();
    const newTag = result.data?.find(t => t.name === tagName);
    if (newTag) {
      handleAddTag(newTag);
    }
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
    // ドロップダウンは開いたままにする
  };

  return (
    <>
      <div className={styles.container} ref={containerRef}>
        {/* 選択済みタグと追加ボタンを同じ行に */}
        <div className={styles.selectedTags}>
          {selectedTags.map((tag) => (
            <span
              key={tag.id}
              className={styles.tagBadge}
              style={tag.color ? { backgroundColor: tag.color } : undefined}
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag.id)}
                disabled={disabled}
                className={styles.removeButton}
                aria-label={`${tag.name}を削除`}
              >
                ×
              </button>
            </span>
          ))}
          {/* タグ追加ボタン - spanでラップ */}
          <span className={styles.addButtonWrapper}>
            <button
              ref={buttonRef}
              type="button"
              onClick={handleToggleDropdown}
              disabled={disabled}
              className={styles.addButton}
            >
              + {brandConfig.labels.tag}を追加
            </button>
          </span>
        </div>
      </div>

      {/* ドロップダウンメニュー - Portal経由でbody直下にレンダリング */}
      {isDropdownOpen && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {/* ヘッダーと閉じるボタン */}
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>{brandConfig.labels.tag}を選択</span>
            <button
              type="button"
              onClick={() => setDropdownOpen(false)}
              className={styles.dropdownCloseButton}
              aria-label="閉じる"
            >
              ✕
            </button>
          </div>

          {availableTags.length > 0 ? (
            <div className={styles.tagList}>
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleAddTag(tag)}
                  className={styles.tagOption}
                  style={tag.color ? { borderLeftColor: tag.color } : undefined}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              {tagsQuery.isLoading ? 'タグを読み込み中...' : '利用可能なタグがありません'}
            </div>
          )}

          <div className={styles.divider} />

          <button
            type="button"
            onClick={handleCreateNew}
            className={styles.createButton}
          >
            + 新規作成
          </button>
        </div>,
        document.body
      )}

      {/* タグ新規作成モーダル */}
      <CreateTagModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onCreated={handleTagCreated}
        position={createModalPosition}
      />
    </>
  );
}
